import React, { useState, useEffect } from 'react';
import { BIBLE_BOOKS, getChaptersForBook, BIBLE_TRANSLATIONS } from '../data/bibleData';
import bibleService from '../services/bibleService';
import Search from './Search';
import Templates from './Templates';

function Sidebar({ studyGuide, updateStudyGuide, settings, onOpenVerseModal }) {
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedVerses, setSelectedVerses] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('bible');
  const [currentTranslation, setCurrentTranslation] = useState(settings?.defaultTranslation || 'KJV');
  const [showVerseModal, setShowVerseModal] = useState(false);

  useEffect(() => {
    if (selectedBook) {
      const bookChapters = getChaptersForBook(selectedBook);
      setChapters(bookChapters);
      setSelectedChapter('');
      setSelectedVerses([]);
    }
  }, [selectedBook]);

  const handleBookChange = (e) => {
    setSelectedBook(e.target.value);
  };

  const handleChapterChange = (e) => {
    setSelectedChapter(e.target.value);
  };

  const loadChapter = async () => {
    if (!selectedBook || !selectedChapter) return;

    setLoading(true);
    try {
      const verses = await bibleService.getChapter(selectedBook, parseInt(selectedChapter), currentTranslation);
      setSelectedVerses(verses);
      onOpenVerseModal(selectedBook, parseInt(selectedChapter), verses, currentTranslation);
    } catch (error) {
      console.error('Error loading chapter:', error);
      setSelectedVerses([]);
    } finally {
      setLoading(false);
    }
  };

  const addVerseToGuide = (verse) => {
    const newSection = {
      id: Date.now(),
      type: 'verse',
      book: selectedBook,
      chapter: parseInt(selectedChapter),
      verse: verse.verse,
      text: verse.text,
      notes: '',
      questions: [],
      observations: ''
    };

    updateStudyGuide({
      sections: [...studyGuide.sections, newSection]
    });
  };

  const handleVerseSelect = (verse) => {
    // Add searched verse to guide
    const newSection = {
      id: Date.now(),
      type: 'verse',
      book: verse.book,
      chapter: verse.chapter,
      verse: verse.verse,
      text: verse.text,
      notes: '',
      questions: [],
      observations: ''
    };

    updateStudyGuide({
      sections: [...studyGuide.sections, newSection]
    });
  };

  const handleApplyTemplate = (templateSections) => {
    updateStudyGuide({
      sections: [...studyGuide.sections, ...templateSections]
    });
  };

  const addSection = (type) => {
    const newSection = {
      id: Date.now(),
      type,
      title: type === 'notes' ? 'Study Notes' : type === 'questions' ? 'Discussion Questions' : 'Observations',
      content: '',
      questions: type === 'questions' ? [] : undefined
    };

    updateStudyGuide({
      sections: [...studyGuide.sections, newSection]
    });
  };

  return (
    <div className="sidebar">
      <div className="sidebar-tabs">
        <button
          className={`tab ${activeTab === 'bible' ? 'active' : ''}`}
          onClick={() => setActiveTab('bible')}
        >
          Bible
        </button>
        <button
          className={`tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          Search
        </button>
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
      </div>

      {activeTab === 'bible' && (
        <>
          <div className="card">
            <h3>Bible Reference</h3>
            <div className="form-group">
              <label>Translation:</label>
              <select
                value={currentTranslation}
                onChange={(e) => setCurrentTranslation(e.target.value)}
              >
                {BIBLE_TRANSLATIONS.map(translation => (
                  <option key={translation.code} value={translation.code}>
                    {translation.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Book:</label>
              <select value={selectedBook} onChange={handleBookChange}>
                <option value="">Select a book</option>
                <optgroup label="Old Testament">
                  {BIBLE_BOOKS.oldTestament.map(book => (
                    <option key={book.name} value={book.name}>{book.name}</option>
                  ))}
                </optgroup>
                <optgroup label="New Testament">
                  {BIBLE_BOOKS.newTestament.map(book => (
                    <option key={book.name} value={book.name}>{book.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            {chapters.length > 0 && (
              <div className="form-group">
                <label>Chapter:</label>
                <select value={selectedChapter} onChange={handleChapterChange}>
                  <option value="">Select a chapter</option>
                  {chapters.map(chapter => (
                    <option key={chapter} value={chapter}>{chapter}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedBook && selectedChapter && (
              <button className="btn btn-primary" onClick={loadChapter} disabled={loading}>
                {loading ? 'Loading...' : 'Load Chapter'}
              </button>
            )}
          </div>

          {selectedVerses.length > 0 && (
            <div className="card">
              <h3>{selectedBook} {selectedChapter} ({selectedVerses.length} verses loaded)</h3>
              <button
                className="btn btn-primary"
                onClick={() => onOpenVerseModal(selectedBook, parseInt(selectedChapter), selectedVerses, currentTranslation)}
              >
                View Verses
              </button>
            </div>
          )}

          <div className="card">
            <h3>Add Section</h3>
            <div className="section-buttons">
              <button className="btn btn-secondary" onClick={() => addSection('notes')}>
                Add Notes
              </button>
              <button className="btn btn-secondary" onClick={() => addSection('questions')}>
                Add Questions
              </button>
              <button className="btn btn-secondary" onClick={() => addSection('observations')}>
                Add Observations
              </button>
            </div>
          </div>

          <div className="card">
            <h3>Study Guide Sections</h3>
            <div className="sections-list">
              {studyGuide.sections.map(section => (
                <div key={section.id} className="section-item">
                  {section.type === 'verse' ? (
                    <span>{section.book} {section.chapter}:{section.verse}</span>
                  ) : (
                    <span>{section.title}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'search' && (
        <div className="card">
          <h3>Search Bible</h3>
          <Search onVerseSelect={handleVerseSelect} currentTranslation={currentTranslation} />
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="card">
          <Templates onApplyTemplate={handleApplyTemplate} />
        </div>
      )}

    </div>
  );
}

export default Sidebar;